import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Paperclip, Send, Sparkles, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import type { MailerComposeMode, MailerDraftInput, MailerDraftResult, MailerSendInput } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Select, Textarea } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { formatBytes, withSignature, withoutSignature } from './compose-utils';
import { RecipientInput, hasInvalidAddress } from './recipient-input';

/** Gmail の送信上限に合わせる（サーバー側でも確かめる） */
const MAX_TOTAL_BYTES = 18 * 1024 * 1024;

export type ComposerInitial = {
  mode: MailerComposeMode;
  to: string[];
  cc: string[];
  subject: string;
  body: string;
  /** 返信・転送の元（Gmail のメッセージ ID） */
  sourceMessageId?: string;
  /** 転送で元のメールに付いている添付の数 */
  sourceAttachmentCount?: number;
};

const MODE_LABELS: Record<MailerComposeMode, string> = {
  new: '新規メッセージ',
  reply: '返信',
  replyAll: '全員に返信',
  forward: '転送',
};

/**
 * メール作成（新規・返信・全員に返信・転送）。
 * 返信の引用と転送の元メールはサーバーで付けるため、ここでは本文だけを書く。
 */
export const Composer = ({
  initial,
  signature,
  safeModeAllowlist,
  onSent,
  onDiscard,
  className,
}: {
  initial: ComposerInitial;
  /** AI 下書きで本文を差し替えても残す署名 */
  signature: string;
  safeModeAllowlist: string[] | null;
  onSent: (result: { threadId: string }) => void;
  onDiscard: () => void;
  className?: string;
}) => {
  const queryClient = useQueryClient();
  const id = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [to, setTo] = useState(initial.to);
  const [cc, setCc] = useState(initial.cc);
  const [bcc, setBcc] = useState<string[]>([]);
  const [showCc, setShowCc] = useState(initial.cc.length > 0);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(initial.subject);
  const [body, setBody] = useState(initial.body);
  const [files, setFiles] = useState<File[]>([]);
  const [includeQuote, setIncludeQuote] = useState(true);
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [tone, setTone] = useState<'polite' | 'concise'>('polite');

  const isReply = initial.mode === 'reply' || initial.mode === 'replyAll';

  // 返信は本文の先頭（署名の前）にカーソルを置く
  useEffect(() => {
    if (!isReply || !bodyRef.current) return;
    bodyRef.current.focus();
    bodyRef.current.setSelectionRange(0, 0);
  }, [isReply]);
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const recipients = [...to, ...cc, ...bcc];
  const blockedBySafeMode = safeModeAllowlist
    ? recipients.filter((email) => {
        const domain = email.split('@')[1] ?? '';
        return !safeModeAllowlist.some((d) => domain === d || domain.endsWith(`.${d}`));
      })
    : [];

  const send = useMutation({
    mutationFn: () => {
      const payload: MailerSendInput = {
        mode: initial.mode,
        to,
        cc,
        bcc,
        subject,
        body,
        sourceMessageId: initial.sourceMessageId,
        includeQuote,
        includeAttachments,
      };
      const form = new FormData();
      form.append('payload', JSON.stringify(payload));
      for (const file of files) form.append('files', file, file.name);
      return api.upload<SingleResponse<{ messageId: string; threadId: string }>>('/mailer/send', form);
    },
    onSuccess: (result) => {
      toast.success('送信しました');
      void queryClient.invalidateQueries({ queryKey: ['mailer'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
      onSent({ threadId: result.data.threadId });
    },
    onError: (error) => toast.error(errorMessage(error), { duration: 10_000 }),
  });

  const draft = useMutation({
    mutationFn: () => {
      const payload: MailerDraftInput = {
        mode: initial.mode,
        sourceMessageId: initial.sourceMessageId,
        instruction,
        tone,
        currentBody: withoutSignature(body),
        subject,
        to,
      };
      return api.post<SingleResponse<MailerDraftResult>>('/mailer/draft', payload);
    },
    onSuccess: (result) => {
      setBody(withSignature(result.data.body, signature));
      // 返信・転送の件名は元のメールに合わせたものを保つ。空のときだけ AI の件名を使う
      if (!subject.trim() || initial.mode === 'new') setSubject(result.data.subject);
      setAiOpen(false);
      toast.success('下書きを作成しました', { description: '内容を確認してから送信してください' });
      bodyRef.current?.focus();
      bodyRef.current?.setSelectionRange(0, 0);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const requestDraft = () => {
    if (initial.mode === 'new' && !instruction.trim() && !withoutSignature(body)) {
      return toast.error('伝えたい要点を入力してください');
    }
    // 書きかけがあれば AI の下書きで置き換わるため確かめる
    if (withoutSignature(body) && !window.confirm('今の本文を AI の下書きで置き換えます。書きかけの内容は清書に使われます。よろしいですか？')) {
      return;
    }
    draft.mutate();
  };

  const trySend = () => {
    if (recipients.length === 0) return toast.error('宛先を入力してください');
    if (hasInvalidAddress(recipients)) return toast.error('形式が正しくないメールアドレスがあります');
    if (!subject.trim() && !window.confirm('件名がありません。このまま送信しますか？')) return;
    if (totalBytes > MAX_TOTAL_BYTES) return toast.error('添付ファイルの合計は 18MB までです');
    send.mutate();
  };

  // Gmail と同じく Ctrl（⌘）+ Enter で送信
  const sendOnShortcut = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      trySend();
    }
  };

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    setFiles((prev) => [...prev, ...Array.from(list)]);
    if (fileInput.current) fileInput.current.value = '';
  };

  return (
    <div className={cn('flex flex-col rounded-card border border-border-strong bg-background', className)}>
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <p className="text-body font-semibold">{MODE_LABELS[initial.mode]}</p>
        <Button variant="ghost" size="icon-sm" onClick={onDiscard} aria-label="閉じる">
          <X aria-hidden />
        </Button>
      </div>

      <div className="px-4">
        <RecipientInput
          id={`${id}-to`}
          label="宛先"
          value={to}
          onChange={setTo}
          autoFocus={initial.mode === 'new' || initial.mode === 'forward'}
          trailing={
            <>
              {!showCc && (
                <button type="button" className="px-1 text-foreground-muted hover:underline" onClick={() => setShowCc(true)}>
                  Cc
                </button>
              )}
              {!showBcc && (
                <button type="button" className="px-1 text-foreground-muted hover:underline" onClick={() => setShowBcc(true)}>
                  Bcc
                </button>
              )}
            </>
          }
        />
        {showCc && <RecipientInput id={`${id}-cc`} label="Cc" value={cc} onChange={setCc} />}
        {showBcc && <RecipientInput id={`${id}-bcc`} label="Bcc" value={bcc} onChange={setBcc} />}
        <div className="flex min-h-touch items-center gap-2 border-b border-border lg:min-h-10">
          <label htmlFor={`${id}-subject`} className="w-10 shrink-0 text-body text-foreground-muted">
            件名
          </label>
          <input
            id={`${id}-subject`}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onKeyDown={sendOnShortcut}
            className="min-w-0 flex-1 bg-transparent py-1.5 text-input outline-none lg:text-body"
          />
        </div>
      </div>

      {aiOpen && (
        <div className="space-y-2 border-b border-border bg-surface px-4 py-3">
          <label htmlFor={`${id}-instruction`} className="flex items-center gap-1.5 text-body font-semibold">
            <Sparkles aria-hidden className="size-4" />
            AI で下書き
          </label>
          <p className="text-note text-foreground-muted">
            {initial.mode === 'new'
              ? '伝えたい要点を書いてください。書きかけの本文があれば、それを清書します。'
              : 'これまでのやり取りを踏まえて書きます。要点は空でもかまいません（例：火曜 14 時で了承、見積りは来週送付）。'}
          </p>
          <div className="flex flex-col gap-2 lg:flex-row">
            <Input
              id={`${id}-instruction`}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  requestDraft();
                }
              }}
              placeholder="伝えたい要点"
              maxLength={1000}
              autoFocus
              className="min-w-0 flex-1"
            />
            <div className="flex gap-2">
              <Select
                value={tone}
                onChange={(e) => setTone(e.target.value as 'polite' | 'concise')}
                aria-label="文体"
                className="w-auto"
              >
                <option value="polite">丁寧に</option>
                <option value="concise">簡潔に</option>
              </Select>
              <Button onClick={requestDraft} disabled={draft.isPending}>
                <Sparkles aria-hidden className={cn(draft.isPending && 'animate-pulse')} />
                {draft.isPending ? '作成中…' : '作成'}
              </Button>
            </div>
          </div>
          <p className="text-note text-foreground-subtle">AI は送信しません。内容を確認・修正してから送信してください。</p>
        </div>
      )}

      <div className="flex-1 px-4 py-3">
        <label htmlFor={`${id}-body`} className="sr-only">
          本文
        </label>
        <Textarea
          ref={bodyRef}
          id={`${id}-body`}
          rows={initial.mode === 'new' ? 14 : 10}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={sendOnShortcut}
          // 本文欄へのドラッグ＆ドロップで添付する
          onDrop={(event) => {
            if (event.dataTransfer.files.length === 0) return;
            event.preventDefault();
            addFiles(event.dataTransfer.files);
          }}
          className="min-h-40 resize-y border-0 px-0 focus-visible:ring-0"
        />

        {isReply && (
          <label className="mt-2 flex items-center gap-2 text-note text-foreground-muted">
            <input
              type="checkbox"
              className="size-4 accent-foreground"
              checked={includeQuote}
              onChange={(e) => setIncludeQuote(e.target.checked)}
            />
            元のメールを引用する
          </label>
        )}
        {initial.mode === 'forward' && (initial.sourceAttachmentCount ?? 0) > 0 && (
          <label className="mt-2 flex items-center gap-2 text-note text-foreground-muted">
            <input
              type="checkbox"
              className="size-4 accent-foreground"
              checked={includeAttachments}
              onChange={(e) => setIncludeAttachments(e.target.checked)}
            />
            元の添付ファイル（{initial.sourceAttachmentCount} 件）も転送する
          </label>
        )}

        {files.length > 0 && (
          <ul className="mt-3 space-y-1">
            {files.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 rounded-control border border-border bg-surface px-2 py-1 text-note"
              >
                <Paperclip aria-hidden className="size-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">{file.name}</span>
                <span className="tabular shrink-0 text-foreground-muted">{formatBytes(file.size)}</span>
                <button
                  type="button"
                  className="rounded-control p-1 hover:bg-muted"
                  onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                  aria-label={`${file.name} を外す`}
                >
                  <X aria-hidden className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {blockedBySafeMode.length > 0 && (
          <p className="mt-3 flex items-start gap-2 rounded-control border-2 border-foreground p-2 text-note font-semibold">
            <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />
            セーフモード中のため {safeModeAllowlist?.join(', ') || '許可ドメイン'} 以外へは送信できません（
            {blockedBySafeMode.join(', ')}）
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-border px-4 py-2">
        <Button onClick={trySend} disabled={send.isPending || blockedBySafeMode.length > 0} title="Ctrl（⌘）+ Enter でも送信できます">
          <Send aria-hidden />
          {send.isPending ? '送信中…' : '送信'}
        </Button>
        <input
          ref={fileInput}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
          aria-label="ファイルを添付"
        />
        <Button variant="ghost" size="icon" onClick={() => fileInput.current?.click()} aria-label="ファイルを添付">
          <Paperclip aria-hidden />
        </Button>
        <Button
          variant={aiOpen ? 'secondary' : 'ghost'}
          onClick={() => setAiOpen((prev) => !prev)}
          aria-expanded={aiOpen}
          aria-label="AI で下書き"
          disabled={draft.isPending}
        >
          <Sparkles aria-hidden />
          <span className="hidden sm:inline">AI で下書き</span>
        </Button>
        <span className="tabular text-note text-foreground-subtle">
          {files.length > 0 && `${formatBytes(totalBytes)} / 18 MB`}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto"
          onClick={() => {
            if (body.trim() && body !== initial.body && !window.confirm('書きかけの内容を破棄しますか？')) return;
            onDiscard();
          }}
          aria-label="破棄"
        >
          <Trash2 aria-hidden />
        </Button>
      </div>
    </div>
  );
};
